/**
 * RoutingFacet — ADR-111 IA-5: the brain's Routing facet. The delegation
 * matrix + specialist capabilities in `agents/<brain>/skills/fleet-roster/
 * SKILL.md` are GENERATED from each specialist's `routing:` block in
 * config/fleet.yaml (ADR-113, scripts/gen-fleet-roster.py) — this facet
 * renders that generated block READ-ONLY, plus a curated-overlay editor for
 * the hand-authored prose outside it (intro, "When to use", disambiguation
 * notes, tie-breaks — ADR-113 D2's "non-mechanical nuance").
 *
 * Authoring routing intent itself happens on each SPECIALIST's own Profile
 * facet (which would regenerate this matrix) — that write path extends
 * POST /api/agents/propose with a `routing:` field and is a deliberate
 * follow-on, NOT implemented here (see the explainer copy below).
 *
 * The brain stays a pure zero-tool router (ADR-085) — this facet only edits a
 * skill FILE describing how it routes; it grants the brain no new tools.
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api, AuthorTestsFailingError } from '../api/client'
import type { FailingCheck, ProposeSkillResult } from '../api/client'
import { FailingChecksPanel } from '../components/FailingChecksPanel'
import { reassembleRoster, splitRoster, type RosterSplit } from './routingDraft'

function em(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}

const rosterPath = (brain: string) => `agents/${brain}/skills/fleet-roster/SKILL.md`

export function RoutingFacet({ agent, canPropose }: { agent: string; canPropose: boolean }) {
  const [loading, setLoading] = useState(true)
  const [loadErr, setLoadErr] = useState<string | null>(null)
  const [found, setFound] = useState(false)
  const [split, setSplit] = useState<RosterSplit | null>(null)
  const [draftBefore, setDraftBefore] = useState('')
  const [draftAfter, setDraftAfter] = useState('')

  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [failingChecks, setFailingChecks] = useState<FailingCheck[]>([])
  const [result, setResult] = useState<ProposeSkillResult | null>(null)

  useEffect(() => {
    let live = true
    setLoading(true)
    setSplit(null)
    setResult(null)
    api.getFleetFile(rosterPath(agent))
      .then(f => {
        if (!live) return
        setFound(f.found)
        const s = splitRoster(f.content)
        setSplit(s)
        setDraftBefore(s.before)
        setDraftAfter(s.after)
      })
      .catch(e => { if (live) setLoadErr(em(e)) })
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [agent])

  const dirty = !!split && (draftBefore !== split.before || draftAfter !== split.after)

  const propose = async () => {
    if (!split) return
    setBusy(true); setErr(null); setFailingChecks([])
    try {
      const content = reassembleRoster(split, draftBefore, draftAfter)
      const r = await api.proposeFleetFile(rosterPath(agent), content, note.trim() || undefined)
      setResult(r)
      setSplit({ ...split, before: draftBefore, after: draftAfter })
    } catch (e) {
      if (e instanceof AuthorTestsFailingError) setFailingChecks(e.checks)
      else setErr(em(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Routing</h1>
        <p className="text-sm text-gray-500 mt-1">
          How {agent} delegates to specialists. The delegation matrix below is GENERATED from each
          specialist's <span className="font-mono">routing:</span> in <span className="font-mono">config/fleet.yaml</span> (ADR-113) — it's
          read-only here. Routing intent itself is authored per-specialist on <em>that specialist's own Profile
          facet</em> (a follow-on — not yet wired up; edit <span className="font-mono">fleet.yaml</span>&apos;s{' '}
          <span className="font-mono">routing:</span> block directly for now). {agent} stays a zero-tool router
          (ADR-085) regardless of what's routed.
        </p>
      </div>

      {loading ? (
        <p className="text-sm text-gray-400 animate-pulse">Loading…</p>
      ) : loadErr ? (
        <p className="text-sm text-red-600">{loadErr}</p>
      ) : !found ? (
        <p className="text-sm text-gray-500 italic">
          No fleet-roster skill found yet at <span className="font-mono">{rosterPath(agent)}</span> — this fleet
          hasn't been migrated to ADR-113 generated routing (or {agent} isn't the brain).
        </p>
      ) : (
        <>
          <div>
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Generated delegation matrix — read-only</div>
            {split && split.hasFence ? (
              <pre className="text-xs text-gray-700 bg-gray-50 border border-gray-100 rounded p-2 whitespace-pre-wrap break-words max-h-80 overflow-auto font-mono">{split.fenced}</pre>
            ) : (
              <p className="text-xs text-gray-500 italic">No generated block found — this skill hasn't been migrated to ADR-113 yet.</p>
            )}
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider block mb-1">
              Header &amp; intro (before the generated matrix)
            </label>
            <textarea
              value={draftBefore}
              onChange={e => setDraftBefore(e.target.value)}
              rows={6}
              className="w-full text-xs font-mono border border-gray-300 rounded px-2 py-1"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider block mb-1">
              Curated notes (after the generated matrix — tie-breaks, disambiguation, pitfalls)
            </label>
            <textarea
              value={draftAfter}
              onChange={e => setDraftAfter(e.target.value)}
              rows={8}
              className="w-full text-xs font-mono border border-gray-300 rounded px-2 py-1"
            />
          </div>

          {err && <p className="text-xs text-red-600 whitespace-pre-wrap break-words">{err}</p>}
          {failingChecks.length > 0 && <FailingChecksPanel checks={failingChecks} />}

          {result && (
            result.improvement_warning ? (
              <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 space-y-1">
                <p>{result.improvement_warning}</p>
              </div>
            ) : (
              <div className="rounded border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-800 space-y-1">
                <p>Sent for review. Nothing changes until you approve it.</p>
                {result.improvement_id
                  ? <Link to={`/improvements/${encodeURIComponent(result.improvement_id)}/pr`} className="font-medium underline">Review &amp; approve in Mission Control →</Link>
                  : <span>Find it under <Link to="/improvements" className="font-medium underline">Improvements</Link>.</span>}
              </div>
            )
          )}

          <div className="flex items-center gap-2">
            {canPropose ? (
              <button type="button" disabled={busy || !dirty} onClick={() => void propose()}
                className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                title={!dirty ? 'No changes to propose' : undefined}>
                {busy ? 'Proposing…' : 'Propose changes'}
              </button>
            ) : <span className="text-xs text-gray-500">Only a fleet owner can propose a routing change.</span>}
            <input type="text" value={note} onChange={e => setNote(e.target.value)}
              placeholder="Optional note for the reviewer" className="flex-1 text-xs border border-gray-200 rounded px-2 py-1" />
          </div>
        </>
      )}
    </div>
  )
}
